import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { useToast } from '@shared/hooks/useToast';
import { WarehouseConnection } from '@domain/inventory/models/WarehouseConnection';
import { useNavigate } from 'react-router-dom';

interface CreateWarehouseParams {
    name: string;
    location?: string;
    warehouseConnections?: Omit<WarehouseConnection, 'id'>[];
}

interface DeleteWarehouseParams {
    id: string;
    options: {
        deleteConnections: boolean;
        deleteMutations: boolean;
    };
}

export const useWarehouses = () => {
    const queryClient = useQueryClient();
    const toast = useToast();
    const navigate = useNavigate();

    const { data: warehouses, isLoading: isLoadingWarehouses, error: warehousesError } = useQuery({
        queryKey: ['warehouses'],
        queryFn: async () => {
            const result = await container.warehouseRepository.getWarehouses();
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        }
    });

    const createWarehouse = useMutation({
        mutationFn: async (params: CreateWarehouseParams) => {
            const result = await container.warehouseRepository.createWarehouse(
                params.name,
                params.location,
                params.warehouseConnections
            );
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['warehouses']);
            queryClient.invalidateQueries(['warehouse-connections']);
            toast.success('Warehouse created successfully');
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to create warehouse');
        }
    });

    const deleteWarehouse = useMutation({
        mutationFn: async (params: DeleteWarehouseParams) => {
            const result = await container.warehouseRepository.deleteWarehouse(
                params.id,
                params.options
            );
            if (result.status === 'error') throw new Error(result.error.message);
            return result.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries(['warehouses']);
            queryClient.invalidateQueries(['warehouse-connections']);
            toast.success('Warehouse deleted successfully');
            navigate('/inventory/warehouses');
        },
        onError: (error: Error) => {
            toast.error(error.message || 'Failed to delete warehouse');
        }
    });

    return {
        warehouses,
        isLoadingWarehouses,
        warehousesError,
        createWarehouse: createWarehouse.mutateAsync,
        deleteWarehouse: deleteWarehouse.mutateAsync,
        isCreating: createWarehouse.isLoading,
        isDeleting: deleteWarehouse.isLoading,
        createError: createWarehouse.error,
        deleteError: deleteWarehouse.error
    };
}; 